import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { Pagination, Tag, theme } from "antd";
import { useState } from "react";
import { MiniCard } from "src/search/components/MiniCard";
import { EntityPreview } from "src/search/components/EntityPreview";
import { Flex } from "./Flex";
import type { OpenEntity } from "src/search/lib/useEntityPreview";

interface ActivityFeedProps {
  data?: {
    data: any[];
    meta?: {
      total?: number;
      page?: number;
      pageSize?: number;
    };
  };
  isLoading?: boolean;
  setPagination: (params: { page: number; pageSize: number }) => void;
  filters?: {
    select?: string;
    project?: string;
    team?: string;
    duration?: string;
    setSelect?: (value: string | undefined) => void;
    setProject?: (value: string | undefined) => void;
    setTeam?: (value: string | undefined) => void;
    setDuration?: (value: string | undefined) => void;
  };
}

export function ActivityFeed({
  data,
  isLoading,
  setPagination,
  filters,
}: ActivityFeedProps) {
  const { token } = theme.useToken();
  const [entityState, setEntityState] = useState<OpenEntity>();
  const entity = {
    uuid: entityState?.uuid,
    list: entityState?.list,
    open: setEntityState,
    close: () => setEntityState(undefined),
  };

  const meta = data?.meta || {};
  const hasMore =
    meta.total != null &&
    meta.page != null &&
    meta.pageSize != null &&
    meta.total > meta.page * meta.pageSize;
  const hasLess = (meta.page || 1) > 1;

  return (
    <>
      <Flex
        direction="column"
        gap={24}
        style={{
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        <div
          style={{
            overflowY: "auto",
            flex: 1,
            paddingBottom: "100px",
            position: "relative",
            width: "100%",
          }}
        >
          {(!data?.data || data?.data.length === 0) && (
            <div style={{ padding: 40, textAlign: "center" }}>
              {isLoading ? <LoadingOutlined /> : <em>no matches</em>}
            </div>
          )}
          {data?.data.map((row, i) => (
            <div
              key={row.identifier}
              style={{
                padding: 4,
                borderTop:
                  i > 0 ? `solid 1px ${token.colorBorderSecondary}` : undefined,
              }}
            >
              <MiniCard
                data={row}
                context={{ entity }}
                style={{ borderRadius: "inherit", padding: "5px 10px" }}
              />
            </div>
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: 0,
            background: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            display: "flex",
          }}
        >
          {(hasMore || hasLess || isLoading) && (
            <Pager
              setPagination={setPagination}
              total={meta.total || 0}
              page={meta.page || 1}
              pageSize={meta.pageSize || 0}
            />
          )}
          {!hasMore && !isLoading && filters && (
            <EndOfResults filters={filters} />
          )}
        </div>
      </Flex>
      <EntityPreview context={{ entity }} />
    </>
  );
}

function Pager({
  page,
  pageSize,
  total,
  setPagination,
}: {
  page: number;
  pageSize: number;
  total: number;
  setPagination(_: { page: number; pageSize: number }): void;
}) {
  return (
    <div style={{ padding: 20 }}>
      <Pagination
        onChange={(page, pageSize) => setPagination({ page, pageSize })}
        current={page}
        pageSize={pageSize}
        total={total}
      />
    </div>
  );
}

function EndOfResults({
  filters,
}: {
  filters: NonNullable<ActivityFeedProps["filters"]>;
}) {
  const { token } = theme.useToken();
  return (
    <Flex
      gap={8}
      start
      inline
      direction="column"
      style={{
        flex: 1,
        padding: 20,
        textAlign: "left",
        fontSize: 13,
        color: token.colorTextSecondary,
        width: "100%",
      }}
    >
      <div>End of activity feed for</div>
      <Flex gap={24}>
        {filters.select && filters.select !== "Event" ? (
          <Tag>
            Type: <strong>{filters.select}</strong>
            <CloseOutlined
              style={{ fontSize: 11, paddingLeft: 4 }}
              onClick={() => filters.setSelect?.(undefined)}
            />
          </Tag>
        ) : (
          <div>
            Type: <strong>Any</strong>
          </div>
        )}
        {filters.project ? (
          <Tag>
            Project: <strong>{filters.project}</strong>
            <CloseOutlined
              style={{ fontSize: 11, paddingLeft: 4 }}
              onClick={() => filters.setProject?.(undefined)}
            />
          </Tag>
        ) : (
          <div>
            Project: <strong>Any</strong>
          </div>
        )}
        {filters.team ? (
          <Tag>
            Team: <strong>{filters.team}</strong>
            <CloseOutlined
              style={{ fontSize: 11, paddingLeft: 4 }}
              onClick={() => filters.setTeam?.(undefined)}
            />
          </Tag>
        ) : (
          <div>
            Team: <strong>Any</strong>
          </div>
        )}
        {["T1W", "", undefined].includes(filters.duration) ? (
          <div>
            When: <strong>This Week</strong>
          </div>
        ) : (
          <Tag>
            When: <strong>{filters.duration}</strong>
            <CloseOutlined
              style={{ fontSize: 11, paddingLeft: 4 }}
              onClick={() => filters.setDuration?.(undefined)}
            />
          </Tag>
        )}
      </Flex>
    </Flex>
  );
}
